import React from 'react';
import { useTranslation } from 'react-i18next';
import { ClewNotice } from './ClewNotice';

/**
 * Public landing page shown before the (heavy) app is loaded.
 *
 * Kept deliberately light: it imports only React + i18n + the standalone
 * `public/data-flow.svg` (via <img>), and MUST NOT transitively import App or
 * any editor/pdf/cloud/host module — that's what lets Root.tsx code-split the
 * whole editor bundle out of the initial download so this page paints fast on
 * mobile data. The single job of the CTA is to hand control to Root's onEnter,
 * which lazy-loads the app and drops the visitor into project selection.
 */

export default function LandingPage({
  onEnter,
  overlay = false,
}: {
  onEnter: () => void;
  /** Rendered on top of the already-mounted app (returned "home"), vs. the
   *  first-load full page. Overlay mode is a fixed, scrollable layer. */
  overlay?: boolean;
}) {
  const { t } = useTranslation();

  const points: Array<{ key: string; title: string; body: string }> = [
    { key: 'device', title: t('landing.points.device.title'), body: t('landing.points.device.body') },
    { key: 'p2p', title: t('landing.points.p2p.title'), body: t('landing.points.p2p.body') },
    { key: 'cloud', title: t('landing.points.cloud.title'), body: t('landing.points.cloud.body') },
  ];

  return (
    <div
      className={`bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
        overlay ? 'fixed inset-0 z-50 overflow-y-auto' : 'min-h-screen'
      }`}
    >
      <div className="w-full max-w-5xl mx-auto px-6 py-10">
        {/* Brand + (when returned home from the app) a quick way back */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL}/logo-filled.png`}
              alt="YAQAT — Yet Another Qualitative Analysis Tool"
              className="h-9 w-auto dark:hidden"
            />
            <img
              src={`${process.env.PUBLIC_URL}/logo-filled-dark.png`}
              alt="YAQAT — Yet Another Qualitative Analysis Tool"
              className="h-9 w-auto hidden dark:block"
            />
          </div>
          {overlay && (
            <button
              type="button"
              onClick={onEnter}
              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t('landing.backToWork')}
            </button>
          )}
        </header>
        <ClewNotice className="mt-6" />

        {/* Hero copy */}
        <div className="mt-10 max-w-3xl">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            {t('landing.headline')}
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            {t('landing.subtext')}
          </p>
        </div>

        {/* Diagram — the hero visual, loaded from the standalone reusable SVG.
            Each SVG commits to one palette; we swap by the app's theme class
            (same as the logos above) so it tracks the Theme setting, not the
            OS colour scheme. */}
        <figure className="mt-10">
          <img
            src={`${process.env.PUBLIC_URL}/data-flow.svg`}
            alt={t('landing.diagramAlt')}
            className="w-full h-auto dark:hidden"
          />
          <img
            src={`${process.env.PUBLIC_URL}/data-flow-dark.svg`}
            alt={t('landing.diagramAlt')}
            className="w-full h-auto hidden dark:block"
          />
        </figure>

        {/* Primary call to action — the focal point after the diagram */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onEnter}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg py-3 px-8 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            {t('landing.cta')}
          </button>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('landing.ctaHint')}</p>
        </div>

        {/* Reassurance points */}
        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {points.map((p) => (
            <div
              key={p.key}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5"
            >
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{p.title}</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{p.body}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('landing.tagline')}
        </p>
      </div>
    </div>
  );
}
